import { useApi, useMount, useModal, useRouter } from "hooks";
import React, { useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import lang from "translations";
import SectionForm from "../section-form/section-form";
import { getSection, editSection, deleteSection } from "apis";
import { sectionDetailResponse, sectionRequest } from "mappers";
import { VenueContext } from "contexts";
import { Path } from "paths";
import { DeleteModal } from "components/modals";
import { Toast, ActionButton } from "components/commons";
import { mapResourcesToZone } from "apis/resource.api";

const EditSection = () => {
  const { id } = useParams();
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;
  const deleteModal = useModal();
  const { history } = useRouter();

  const { request: getRequest, mappedData: initialData, loading, error } = useApi({
    api: getSection,
    mapper: sectionDetailResponse,
    params: { sectionId: id, venueId },
    handleOwnError: true,
  });

  const { request: editRequest, loading: submitting } = useApi({
    api: editSection,
    handleOwnError: { badrequest: true },
    paramsMapper: sectionRequest,
  });

  const { request: requestDeleteSection } = useApi({
    api: () => deleteSection({ sectionId: id }),
  });

  useMount(() => {
    getRequest({ sectionId: id, venueId });
    // Add analytics or tracking if needed
  });

  const submitForm = useCallback(
    async (params) => {
      try {
        const res = await editRequest({ id, ...params });
        // After edit, call resource mapping API
        await mapResourcesToZone({
          venueId,
          zoneId: id,
          resourceIds: params.resourceIds || [],
        });
        history.push(Path.SECTION);
        return {
          response: res,
          message: lang.populate(lang.sectionUpdated, [params.name]),
        };
      } catch (e) {
        throw e;
      }
    },
    [editRequest, id, history, venueId]
  );

  const handleDelete = useCallback(async () => {
    try {
      await requestDeleteSection();
      Toast.success(lang.sectionDeleted);
      history.push(Path.SECTION);
    } catch (e) {
      Toast.error(e.message || lang.failedToDeleteSection);
    }
  }, [requestDeleteSection, history]);

  const deleteSectionButton = (
    <ActionButton
      type="button"
      variant="danger"
      text={lang.deleteSection}
      onClick={deleteModal.open}
    />
  );

  return (
    <>
      <SectionForm
        submit={submitForm}
        title={lang.editSection}
        submitting={submitting}
        initialData={initialData}
        loading={loading}
        error={error}
        deleteSectionButton={deleteSectionButton}
        sectionId={id}
      />
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onDelete={handleDelete}
        title={lang.deleteSection}
        description={lang.areYouSure || "Are you sure you want to delete this section? This action cannot be undone."}
      />
    </>
  );
};

export default EditSection;